import React from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import lodashCompact from 'lodash/fp/compact';
import { Formik, Form } from 'formik';

import { formatPrice } from 'common/formatters';

import { createFormValidationFn } from 'client/helpers/formikHelpers';

import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import ButtonDeprecated, {
  BUTTON_THEME_YELLOW_GREYCLIFF,
} from 'client/components/ButtonDeprecated';

const getFormInitialValues = ({ tourId, floorPlans }) => {
  return {
    tourId: tourId,
    renterFeedback: floorPlans.map((floorPlan) => ({
      listingId: floorPlan.showUnits[0].id,
      positiveFeedback: '',
      negativeFeedback: '',
      questions: '',
      rating: null,
    })),
  };
};

const getFloorPlanFormFields = ({ index }) => {
  return lodashCompact([
    index === 0 && {
      id: 'tourId',
      type: 'hidden',
    },
    {
      id: `renterFeedback[${index}].listingId`,
      type: 'hidden',
    },
    {
      id: `renterFeedback[${index}].positiveFeedback`,
      title: "Renter's positive feedback",
      type: 'long_text',
      properties: {
        placeholder: 'Write here...',
      },
    },
    {
      id: `renterFeedback[${index}].negativeFeedback`,
      title: "Renter's negative feedback",
      type: 'long_text',
      properties: {
        placeholder: 'Write here...',
      },
    },
    {
      id: `renterFeedback[${index}].renterQuestions`,
      title: "Renter's questions for us?",
      type: 'long_text',
      properties: {
        placeholder: 'Example: Is the landlord fine with their 65lb dog?',
      },
    },
    {
      id: `renterFeedback[${index}].rating`,
      title: 'Rating (5 stars = they love it)',
      type: 'star_rating',
      validations: ['required'],
    },
  ]);
};

// written note or star rating required!!
const getFormFields = ({ floorPlans }) => {
  return [
    ...floorPlans.reduce((formFields, listing, index) => {
      formFields.push(...getFloorPlanFormFields({ index }));
      return formFields;
    }, []),
  ];
};

const SrLocatorToursFeedback = ({ tourId, floorPlans, onSubmit }) => (
  <Formik
    onSubmit={onSubmit}
    initialValues={getFormInitialValues({ tourId, floorPlans })}
    validate={createFormValidationFn(getFormFields({ floorPlans }))}
    render={(formikBag) => (
      <Form>
        {floorPlans.map((floorPlan, index) => {
          const [listing] = floorPlan.showUnits;
          return (
            <SrBox
              key={floorPlan.id}
              padding="15px"
              marginTop="10px"
              boxShadow="0px 9px 18px rgba(25, 51, 64, 0.07)"
              borderRadius="14px"
            >
              <SrFlex justify="between">
                <SrFlex>
                  <img
                    width="80"
                    height="75"
                    alt={listing.sunroomPhotoUrls[0]}
                    src={listing.sunroomPhotoUrls[0]}
                    style={{ borderRadius: '7px', marginRight: '10px' }}
                    className="dropshadow-bottom-edge"
                  />
                  <SrFlex justify="between" direction="col">
                    <SrText display="block" weight="bolder" size="larger" font="greycliff">
                      {listing.apartmentUnit}
                    </SrText>
                    <SrText
                      display="block"
                      weight="medium"
                      size="medium"
                      font="greycliff"
                      spacing="41"
                    >
                      {[
                        pluralize('Bed', floorPlan.bedrooms, true),
                        pluralize('Bath', floorPlan.bathrooms, true),
                        `${floorPlan.squareFeet} ft.`,
                      ].map((text) => (
                        <SrBox key={text} display="inlineBlock" paddingRight="10px">
                          {text}
                        </SrBox>
                      ))}
                    </SrText>
                    <SrText color="grey600">
                      {floorPlan.otherUnits.length === 0 ? (
                        <span>&nbsp;</span>
                      ) : (
                        `Same floorplan as: ${floorPlan.otherUnits.map(
                          ({ apartmentUnit }) => apartmentUnit,
                        )}`
                      )}
                    </SrText>
                  </SrFlex>
                </SrFlex>
                <SrText color="green" size="larger" weight="medium">
                  {formatPrice(listing.price)}
                </SrText>
              </SrFlex>
              {getFloorPlanFormFields({ index }).map((formField) => (
                <SrFormikField
                  key={formField.id}
                  formField={formField}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
              ))}
            </SrBox>
          );
        })}
        <SrBox
          position="fixed"
          bottom="0"
          left="0"
          width="100%"
          boxShadow="0px -6px 17px rgba(219, 176, 0, 0.14)"
        >
          <SrBox
            width="100%"
            margin="0 auto"
            paddingBottom="43px"
            paddingTop="7px"
            className="greyBlur-90 border-box"
            paddingRight="10px"
            paddingLeft="10px"
          >
            <ButtonDeprecated
              theme={BUTTON_THEME_YELLOW_GREYCLIFF}
              disabled={!formikBag.isValid}
              type="submit"
            >
              Submit Renter Feedback
            </ButtonDeprecated>
          </SrBox>
        </SrBox>
      </Form>
    )}
  />
);

SrLocatorToursFeedback.propTypes = {
  tourId: PropTypes.number.isRequired,
  floorPlans: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

SrLocatorToursFeedback.displayName = 'SrLocatorToursFeedback';

export default SrLocatorToursFeedback;
